import { memo, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

/** Spinner size options */
type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl'

/** Spinner color options */
type SpinnerColor = 'primary' | 'white' | 'gray'

/** Props for Spinner component */
interface SpinnerProps {
  size?: SpinnerSize
  className?: string
  color?: SpinnerColor
}

/** Props for FullPageSpinner component */
interface FullPageSpinnerProps {
  message?: string
}

const sizeStyles: Record<SpinnerSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
  xl: 'h-12 w-12'
}

const colorStyles: Record<SpinnerColor, string> = {
  primary: 'text-primary-600 dark:text-primary-400',
  white: 'text-white',
  gray: 'text-gray-400 dark:text-gray-500'
}

const SpinnerBase = memo(function Spinner({ size = 'md', className = '', color = 'primary' }: SpinnerProps): ReactElement {
  const { t } = useTranslation()
  return (
    <svg
      className={`animate-spin ${sizeStyles[size]} ${colorStyles[color]} ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      role="status"
      aria-label={t('common.loading')}
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )
})

function FullPageSpinner({ message }: FullPageSpinnerProps): ReactElement {
  const { t } = useTranslation()
  const displayMessage = message || t('common.loading')
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-white dark:bg-gray-900 z-50">
      <SpinnerBase size="xl" />
      <p className="mt-4 text-gray-600 dark:text-gray-400">{displayMessage}</p>
    </div>
  )
}

const Spinner = Object.assign(SpinnerBase, {
  FullPage: FullPageSpinner
})

export default Spinner
